/**
 * Загрузить семантику файлом.
 *
 * Клиент приносит то, что у него уже есть: выгрузку из Вордстата, Кей Коллектора
 * или таблицу, которую агентство собирало руками. Наше дело — принять её,
 * ничего не потеряв молча, и честно сказать, что с ней будет.
 *
 * Три правила, которые определили устройство экрана.
 *
 * 1. **Ничего не режется молча.** Ни лишние столбцы, ни повторы, ни строки, не
 *    поместившиеся в тариф. Каждое число названо словом и стоит рядом с тем, что
 *    из-за него не попадёт в ядро.
 * 2. **Формат клиента, а не наш.** Столбец любой, заголовок не обязателен,
 *    кодировка любая из двух живых, операторы поисковых сервисов снимаются сами.
 *    Шаблон скачивать не надо: у человека уже есть файл, и он не должен его
 *    переделывать под нас.
 * 3. **Ядро меняется явным действием, и последствие названо до нажатия.**
 *    Замена ядра — граница версий в истории графиков, ровно как пересборка, и
 *    сказать об этом после значит не сказать вовсе.
 */

import { useRef, useState } from 'react';

import { api } from '../../api/client';
import { ErrorNotice } from '../../ui/state';
import { Confirm, TextArea } from '../../ui/fields';
import { countOf, int } from '@shared/src/format';
import {
  MAX_FILE_BYTES,
  MAX_TEXT_LENGTH,
  MIN_TEXT_LENGTH,
  SemanticsError,
  parseFile,
  parseText,
  type ParsedSemantics,
} from './semantics';
import type { components } from '../../api/schema';

type BulkResult = components['schemas']['QuestionBulkResult'];

type Mode = 'append' | 'replace';

/** Потолок одной пачки в контракте. Больше — это уже не одна ревизия ядра. */
const BATCH_LIMIT = 500;

/** Почему файл не прочитался — словами, а не кодом. */
const FILE_TROUBLE: Record<string, string> = {
  'too-big': `Файл больше ${int(MAX_FILE_BYTES / 1024 / 1024)} мегабайт. Столько строк в ядро всё равно не поместится: оставьте самые важные запросы.`,
  'old-excel':
    'Это старый формат Экселя (xls). Откройте файл и сохраните его как xlsx или csv — мы прочитаем оба.',
  'not-a-workbook':
    'Книгу Экселя прочитать не удалось. Сохраните лист как csv, так надёжнее.',
  'not-a-zip': 'Книгу Экселя прочитать не удалось. Сохраните лист как csv, так надёжнее.',
  'unsupported-compression':
    'Книга сжата способом, которого мы не знаем. Сохраните лист как csv, так надёжнее.',
  zip64:
    'Книга собрана по расширенному правилу архива, которого мы не читаем. Сохраните лист как csv, так надёжнее.',
  'old-browser':
    'Ваш браузер не умеет распаковывать книгу Экселя. Обновите его или сохраните лист как csv — csv читается везде.',
};

const FILE_TROUBLE_FALLBACK =
  'Файл прочитать не удалось. Подойдут xlsx, csv, tsv и txt — либо просто вставьте столбец в поле ниже.';

/**
 * Что скажет разбор про сам файл: столбец, заголовок, повторы, короткие строки.
 *
 * Каждое из этих чисел объясняет, почему строк стало меньше, чем человек видел в
 * таблице. Ноль не печатается: «убрано 0 повторов» — это шум, за которым теряется
 * настоящая причина.
 */
function parseNotes(parsed: ParsedSemantics): string[] {
  const notes: string[] = [];
  if (parsed.column > 0) {
    notes.push(
      `в файле ${countOf(parsed.columns, 'столбец', 'столбца', 'столбцов')}, фразы взяли из ${int(parsed.column)}-го`,
    );
  }
  if (parsed.header) notes.push(`строку заголовка «${parsed.header}» пропустили`);
  if (parsed.duplicates) {
    notes.push(`повторов убрали ${int(parsed.duplicates)}`);
  }
  if (parsed.tooShort.length) {
    notes.push(
      `${countOf(parsed.tooShort.length, 'строка короче', 'строки короче', 'строк короче')} ${int(MIN_TEXT_LENGTH)} знаков не подходит: такой вопрос нейросети нечего искать`,
    );
  }
  if (parsed.tooLong.length) {
    notes.push(
      `${countOf(parsed.tooLong.length, 'строка длиннее', 'строки длиннее', 'строк длиннее')} ${int(MAX_TEXT_LENGTH)} знаков не подходит: столько в вопрос не помещается`,
    );
  }
  if (parsed.truncated) notes.push('файл очень длинный, взяли начало');
  return notes;
}

export function Import({
  brandId,
  free,
  own,
  replaceRoom,
  onDone,
  onCancel,
}: {
  brandId: string;
  /** Сколько вопросов ещё помещается в тариф. `null` — квота роли не видна. */
  free: number | null;
  /**
   * Сколько вопросов в ядре бренда сейчас, если экран знает это наверняка.
   *
   * `null` — не знает: список пришёл страницей или под фильтром. Тогда число не
   * называется вовсе. Сказать «выключатся 200 вопросов» бренду, у которого их
   * четыреста, хуже, чем не назвать числа: человек соглашается на одно, а
   * теряет другое.
   */
  own: number | null;
  /**
   * Сколько строк примет замена. `null` — посчитать нечем, решает сервер.
   *
   * Приходит готовым числом, а не считается здесь: места тарифа знает экран
   * ядра, у которого лежат сырые числа квоты, и вторая арифметика по тем же
   * данным разошлась бы с первой на первой же правке.
   */
  replaceRoom: number | null;
  /** Второй довод — заменой ли была загрузка: у пустого ядра замена выключает ноль. */
  onDone: (result: BulkResult, replaced: boolean) => void;
  onCancel: () => void;
}) {
  const [parsed, setParsed] = useState<ParsedSemantics | null>(null);
  const [dropped, setDropped] = useState<Set<string>>(new Set());
  const [pasted, setPasted] = useState('');
  const [mode, setMode] = useState<Mode>('append');
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [over, setOver] = useState(false);
  const chooser = useRef<HTMLInputElement>(null);
  /* Номер последнего разбора. Чтение книги Экселя идёт асинхронно, и человек за
     это время успевает выбрать другой файл или вставить столбец руками. Без
     номера медленный разбор ПЕРВОГО файла возвращается последним и подменяет
     предпросмотр: в поле вставки одно, на экране другое, а на сервер уходит
     третье. Побеждает последнее действие человека, а не то, что дочиталось. */
  const attempt = useRef(0);

  /* Сколько строк можно записать этой загрузкой.
     У замены потолок свой: она освобождает места нынешнего ядра, и клиент,
     выбравший тариф целиком, обязан суметь заменить ядро на такое же по размеру.
     Считать это молча нельзя — превышение квоты сервер отвергает ПАЧКОЙ ЦЕЛИКОМ,
     и обещанные экраном пятьсот строк вернулись бы отказом, при котором не
     запишется ни одна, даже первая сотня допустимых.
     Пусто — считать нечем: под фильтром и на неполном списке размер ядра
     неизвестен. Тогда потолок остаётся за сервером: его отказ называет обе цифры
     словами, а выдуманное экраном число молча срезало бы годные строки.
     Потолок пачки — граница контракта: одна загрузка это одна ревизия ядра. */
  const capacity = mode === 'replace' ? replaceRoom : free;
  const room = capacity === null ? BATCH_LIMIT : Math.min(capacity, BATCH_LIMIT);

  const picked = parsed ? parsed.rows.filter((text) => !dropped.has(text)) : [];
  const sending = picked.slice(0, room);
  const overflow = picked.length - sending.length;
  const notes = parsed ? parseNotes(parsed) : [];
  // Что не поместится, считается по ОТОБРАННОМУ, а не по строке файла: человек
  // снимает галочки, и место занимает только то, что уходит на сервер.
  const spare = new Set(picked.slice(room));

  function accept(result: ParsedSemantics) {
    attempt.current += 1;
    setTrouble(null);
    setError(null);
    setDropped(new Set());
    setParsed(result);
  }

  async function readFile(file: File) {
    const ticket = (attempt.current += 1);
    try {
      const result = await parseFile(file);
      if (attempt.current !== ticket) return;
      // Поле вставки очищается вместе с приходом файла: два источника на экране
      // одновременно читаются как «загрузится и то, и другое».
      setPasted('');
      accept(result);
    } catch (cause) {
      if (attempt.current !== ticket) return;
      setParsed(null);
      setTrouble(
        cause instanceof SemanticsError
          ? (FILE_TROUBLE[cause.message] ?? FILE_TROUBLE_FALLBACK)
          : FILE_TROUBLE_FALLBACK,
      );
    }
  }

  async function send() {
    setAsked(false);
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<BulkResult>(`/brands/${brandId}/questions/bulk`, {
        questions: sending.map((text) => ({ text })),
        origin: 'imported',
        mode,
      });
      onDone(result, mode === 'replace');
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  if (busy) {
    return (
      <div className="panel">
        <h2 className="panel-h">Записываем семантику в ядро</h2>
        <p className="stamp">
          {mode === 'replace'
            ? 'Выключаем прежние вопросы и записываем ваши.'
            : 'Добавляем ваши вопросы к тем, что уже есть.'}
        </p>
      </div>
    );
  }

  return (
    <div className="panel">
      <h2 className="panel-h">Загрузить семантику</h2>
      <p className="stamp">
        Один столбец с запросами. Заголовок не обязателен, лишние столбцы мы пропустим
        сами. Подойдут xlsx, csv, tsv и txt — или просто скопируйте столбец из Экселя или
        Гугл-таблицы и вставьте в поле ниже.
      </p>

      {trouble ? (
        <div className="notice warn" role="status">
          <b>Файл не прочитался</b>
          {trouble}
        </div>
      ) : null}

      {error ? <ErrorNotice error={error} /> : null}

      {/* Область броска — не единственный путь: то же делает кнопка и вставка.
          Бросок мышью недоступен с клавиатуры, и подменять им выбор файла
          значило бы закрыть загрузку части клиентов. */}
      <div
        className={over ? 'dropzone over' : 'dropzone'}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          const file = event.dataTransfer.files[0];
          if (file) void readFile(file);
        }}
      >
        <p>Перетащите сюда файл с семантикой</p>
        <input
          ref={chooser}
          type="file"
          accept=".xlsx,.csv,.tsv,.txt,text/csv,text/plain"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void readFile(file);
            event.target.value = '';
          }}
        />
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          onClick={() => chooser.current?.click()}
        >
          Выбрать файл
        </button>
      </div>

      <TextArea
        label="Или вставьте столбец сюда"
        value={pasted}
        onChange={(value) => {
          setPasted(value);
          if (value.trim()) accept(parseText(value));
          else {
            attempt.current += 1;
            setParsed(null);
          }
        }}
        rows={4}
        hint="Скопируйте столбец в таблице и вставьте: одна фраза в строке"
      />

      {parsed ? (
        <>
          <p className="stamp">
            Нашли {countOf(parsed.rows.length, 'фразу', 'фразы', 'фраз')}
            {notes.length ? `: ${notes.join(', ')}` : ''}.
          </p>

          <div className="filters">
            <label className="check">
              <input
                type="radio"
                name="import-mode"
                checked={mode === 'append'}
                onChange={() => setMode('append')}
              />
              <span>Добавить к ядру</span>
            </label>
            <label className="check">
              <input
                type="radio"
                name="import-mode"
                checked={mode === 'replace'}
                onChange={() => setMode('replace')}
              />
              <span>Заменить ядро целиком</span>
            </label>
          </div>
          <p className="stamp">
            {mode === 'replace'
              ? `${own === null ? 'Нынешние вопросы' : `Нынешние ${countOf(own, 'вопрос', 'вопроса', 'вопросов')}`} выключатся, и их места освободятся. Это новая версия ядра: сравнивать «до» и «после» через неё нельзя.`
              : 'Загруженное встанет рядом с тем, что уже есть. Повторы мы отбросим.'}
          </p>

          {overflow > 0 ? (
            <div className="notice warn" role="status">
              <b>Помещается не всё</b>
              {`Отобрано ${countOf(picked.length, 'фраза', 'фразы', 'фраз')}, а мест ${int(room)}. Запишем первые ${int(room)}, остальные ${int(overflow)} останутся в файле. Чтобы взять их тоже, расширьте тариф или выключите лишнее в ядре.`}
            </div>
          ) : null}

          <div className="tbl-wrap">
            <table className="mini">
              <caption>
                Пойдёт в ядро {countOf(sending.length, 'фраза', 'фразы', 'фраз')}
                {dropped.size ? ` · снято ${int(dropped.size)}` : ''}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Запрос покупателя</th>
                  <th scope="col">В ядро</th>
                </tr>
              </thead>
              <tbody>
                {parsed.rows.map((text) => (
                  <tr key={text}>
                    <td>
                      <span className="q-text">{text}</span>
                      {spare.has(text) ? (
                        <span className="stamp">не поместится в тариф</span>
                      ) : null}
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Записать в ядро: ${text}`}
                        checked={!dropped.has(text)}
                        onChange={(event) => {
                          const next = new Set(dropped);
                          if (event.target.checked) next.delete(text);
                          else next.add(text);
                          setDropped(next);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      <div className="row-actions">
        <button
          className="btn btn-primary"
          type="button"
          disabled={sending.length === 0}
          onClick={() => (mode === 'replace' ? setAsked(true) : void send())}
        >
          {mode === 'replace' ? 'Заменить ядро' : 'Добавить в ядро'}
        </button>
        <button className="btn btn-ghost" type="button" onClick={onCancel}>
          Отмена
        </button>
      </div>

      <Confirm
        open={asked}
        title="Заменить ядро загруженной семантикой"
        action="Заменить ядро"
        danger
        onCancel={() => setAsked(false)}
        onConfirm={() => void send()}
      >
        <p>
          {own === null
            ? 'Нынешние вопросы ядра'
            : `Нынешние ${countOf(own, 'вопрос', 'вопроса', 'вопросов')}`}{' '}
          выключатся целиком — вместе с правками текстов. Собранные по ним ответы и снимки
          остаются на месте.
        </p>
        <p>
          Это новая версия ядра, то есть граница в истории графиков. Сравнивать «до» и
          «после» через неё нельзя: разница в видимости объясняется сменой вопросов не
          меньше, чем вашей работой.
        </p>
      </Confirm>
    </div>
  );
}
